import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin } from 'antd'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  MetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderFoodMoodEvent } from '../../helpers'
import {
  useDeleteFoodMoodMutation,
  useFoodMoodsQuery,
  useSaveFoodMoodsMutation,
} from '../../hooks'
import {
  addFoodMood,
  renameFoodMood,
  resetFoodMoodTree,
  setDataForRenamingFoodMood,
} from '../../redux/actions'

const FoodMoodsPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle(APP_ROUTES.MOBILE_APP_ONLY.subMenuItems.FOOD_MOODS.name)

  const eventEntityTypes = useMemo(() => [EVENT_METADATA_TYPES.FOOD_MOOD], [])

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useFoodMoodsQuery()

  const [saveFoodMoods, { isLoading: isSaveLoading, error: saveError }] =
    useSaveFoodMoodsMutation(refetchLastEvent)

  const [deleteFoodMood] = useDeleteFoodMoodMutation(refetchLastEvent)

  const { isDirty, data, dataForRenaming } = useSelector(
    state => state.foodMoods.foodMoods,
  )

  const hasFullPermission = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.SAVE,
  )

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(node => dispatch(addFoodMood(node)), [dispatch])

  const handleDelete = useCallback(
    ({ id }) => deleteFoodMood(id),
    [deleteFoodMood],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameFoodMood({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingFoodMood({ id })),
    [dispatch],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetFoodMoodTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveFoodMoods(data),
    [data, saveFoodMoods],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'FoodMoods error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  return isFetchLoading || isSaveLoading || isFetchLatestEventLoading ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Food Moods</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderFoodMoodEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={!isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <MetadataTree
        disableLink={!hasPermissionToViewDetails}
        metadataType={METADATA_TYPES.FOOD_MOODS}
        isDirty={isDirty}
        showSearch
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        setDataForRenaming={handleSetDataForRenaming}
        dataForRenaming={dataForRenaming}
        getLink={({ node: { id } }) =>
          `${APP_ROUTES.MOBILE_APP_ONLY.subMenuItems.FOOD_MOODS.link}/${id}`
        }
      />
    </>
  )
}

export default FoodMoodsPage
